/**
 * SOLID Review
 * S — Strong. One concern: the 9:16 frame's dimensions — its size in the window, the three zones' height and the ad slot's — as the
 *     window and the frame's element change size.
 * O — Good. A new zone count is the view's argument.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. The dimensions.
 * D — Strong. The platform's viewport, resize observer and page events; the frame's element its view hands it.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { Reactions } from './Reactions';
import type { IConfigDepViewPlatform } from './platform/IConfigDepViewPlatform';

const ASPECT_RATIO_WIDTH = 9;
const ASPECT_RATIO_HEIGHT = 16;
const MAX_AD_HEIGHT_PX = 90;
const MIN_AD_HEIGHT_PX = 50;
const AD_HEIGHT_PERCENT = 0.10;
/** Below this the zones are too small to play in: the frame keeps this height and the page scrolls. */
const MIN_PLAYABLE_HEIGHT_PX = 560;

/** The frame's dimensions. */
export interface IStateLayoutDimensions {
  readonly containerWidth: number;
  readonly containerHeight: number;
  readonly zoneHeight: number;
  readonly adHeight: number;
  readonly isConstrained: boolean;
  readonly needsScroll: boolean;
}

/**
 * THE MOBILE FRAME (frontend-framework-oo P5, 2026-10-30, §28.297) — `components/layout/MobileLayout.tsx`'s state and effect: the frame
 * is 9:16 inside the window (constrained by its height when the window is wider), never below a playable height; the ad slot is a tenth
 * of it, between 50 and 90 px; the zones share the rest. Measured when the frame is made, then on every resize and turn of the window
 * and every size change of the frame's own element.
 */
export class MobileLayoutViewModel implements IServiceViewModel<IStateLayoutDimensions> {
  private readonly state: ObservableState<IStateLayoutDimensions>;
  private readonly reactions = new Reactions<null>([{
    deps: () => [],
    run: () => {
      const { pageEvents, resizeObserver } = this.platform;
      const handleResize = (): void => this.measure();
      handleResize();
      const element = this.frame.current;
      const stopObserving = element && resizeObserver.available() ? resizeObserver.observe(element, handleResize) : null;
      const leaveResize = pageEvents.on('resize', handleResize);
      const leaveTurn = pageEvents.on('orientationchange', handleResize);
      return () => {
        leaveResize();
        leaveTurn();
        if (stopObserving) stopObserving();
      };
    },
  }]);

  constructor(
    private readonly platform: Pick<IConfigDepViewPlatform, 'viewport' | 'resizeObserver' | 'pageEvents'>,
    private readonly frame: { readonly current: Element | null },
    private readonly zoneCount: number,
  ) {
    this.state = new ObservableState<IStateLayoutDimensions>(this.dimensions());
  }

  static dimensionsOf(viewportWidth: number, viewportHeight: number, zoneCount = 3): IStateLayoutDimensions {
    const aspectRatio = ASPECT_RATIO_WIDTH / ASPECT_RATIO_HEIGHT;
    const viewportAspect = viewportWidth / viewportHeight;
    let containerWidth: number;
    let containerHeight: number;
    let isConstrained = false;
    if (viewportAspect > aspectRatio) {
      containerHeight = viewportHeight;
      containerWidth = containerHeight * aspectRatio;
      isConstrained = true;
    } else {
      containerWidth = viewportWidth;
      containerHeight = containerWidth / aspectRatio;
    }
    if (containerHeight < MIN_PLAYABLE_HEIGHT_PX) {
      containerHeight = MIN_PLAYABLE_HEIGHT_PX;
      containerWidth = Math.min(containerHeight * aspectRatio, viewportWidth);
    }
    const needsScroll = containerHeight > viewportHeight;
    const rawAdHeight = containerHeight * AD_HEIGHT_PERCENT;
    const adHeight = Math.min(MAX_AD_HEIGHT_PX, Math.max(MIN_AD_HEIGHT_PX, rawAdHeight));
    const zoneHeight = (containerHeight - adHeight) / zoneCount;
    return { containerWidth, containerHeight, zoneHeight, adHeight, isConstrained, needsScroll };
  }

  private dimensions(): IStateLayoutDimensions {
    return MobileLayoutViewModel.dimensionsOf(this.platform.viewport.width(), this.platform.viewport.height(), this.zoneCount);
  }

  private measure(): void {
    this.state.update(this.dimensions());
  }

  getSnapshot(): IStateLayoutDimensions {
    return this.state.get();
  }

  subscribe(listener: () => void): () => void {
    return this.state.subscribe(listener);
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(): void {
    this.reactions.stopChanged(null);
  }

  commit(): void {
    this.reactions.startChanged(null);
  }
}
